import { useSelector } from 'react-redux';
import { Dispatch, SetStateAction } from 'react';
import Button from './Button';
import { CloseIcon } from '../assets/svg/MenuIcons';

interface LegalPrivacyModalProps {
  isLegalPrivacyModalOpen: boolean;
  setIsLegalPrivacyModalOpen: Dispatch<SetStateAction<boolean>>;
  onClose?: () => void;
}

function LegalPrivacyModal({
  isLegalPrivacyModalOpen,
  setIsLegalPrivacyModalOpen,
  onClose,
}: LegalPrivacyModalProps) {
  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );

  const { entries } = contentfulData;
  const { rb_links, rb_more } = entries;

  // Get popup contents from rb_more or use defaults
  const popupData = rb_more?.rbMoreWebLegalPrivacyPopup || {
    modalTitle: 'Leaving the Rogers Bank Website',
    message: 'Would you like to check the FAQs by leaving this website?',
    buttons: [
      {
        label:
          'Electronic Communications and Digital Services Terms & Conditions',
      },
      {
        label: 'Cardholder Legal Documents',
      },
      {
        label: 'Privacy Policies',
      },
    ],
  };

  const handleElectronicTermsClick = () => {
    setIsLegalPrivacyModalOpen(false);
    window.open(
      rb_links?.rbLinksCollection?.more_digital_terms_and_conditions_pdf,
      '_blank'
    );
    if (onClose) {
      onClose();
    }
  };

  const handleCardholderLegalClick = () => {
    setIsLegalPrivacyModalOpen(false);
    window.open(
      rb_links?.rbLinksCollection?.more_rewards_terms_and_conditions_link,
      '_blank'
    );
    if (onClose) {
      onClose();
    }
  };

  const handlePrivacyPoliciesClick = () => {
    setIsLegalPrivacyModalOpen(false);
    window.open(rb_links?.rbLinksCollection?.privacy_policy, '_blank');
    if (onClose) {
      onClose();
    }
  };

  const handleCancelClose = () => {
    setIsLegalPrivacyModalOpen(false);
    if (onClose) {
      onClose();
    }
  };

  if (!isLegalPrivacyModalOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      aria-labelledby="modal-title"
      aria-modal="true"
      role="dialog"
    >
      <div
        className="bg-white p-5 md:rounded-lg rounded-none max-w-sm md:max-w-md mx-auto relative"
        role="alert"
      >
        <div
          className="absolute top-5 right-5"
          onClick={handleCancelClose}
          aria-label="closeIcon"
        >
          <CloseIcon width={'24'} height="24" fill="#1F1F1F" />
        </div>

        <div className="inline-flex p-5 flex-col items-start gap-5 bg-white rounded-[15px]">
          <div className="text-center w-full">
            <div
              className="mb-4 text-[#1F1F1F] text-[22px] font-[650] leading-[28px] tracking-[0.077px]"
              aria-label={popupData.modalTitle}
            >
              {popupData.modalTitle}
            </div>
            <p
              className="mb-5 text-[#1F1F1F] text-[16px] font-[500] leading-[20px] tracking-[-0.051px]"
              aria-label={popupData.message}
            >
              {popupData.message}
            </p>
          </div>

          <div className="flex flex-col gap-3 md:gap-5 w-full">
            <Button
              variant="outline"
              onClick={handleElectronicTermsClick}
              className="min-h-16 px-4 py-4 w-full text-center text-sm leading-tight"
              ariaLabel={popupData.buttons[0]?.label}
            >
              {popupData.buttons[0]?.label}
            </Button>

            <Button
              variant="outline"
              onClick={handleCardholderLegalClick}
              className="min-h-16 px-4 py-4 w-full text-center text-sm leading-tight"
              ariaLabel={popupData.buttons[1]?.label}
            >
              {popupData.buttons[1]?.label}
            </Button>

            <Button
              variant="outline"
              onClick={handlePrivacyPoliciesClick}
              className="min-h-16 px-4 py-4 w-full text-center text-sm leading-tight"
              ariaLabel={popupData.buttons[2]?.label}
            >
              {popupData.buttons[2]?.label}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LegalPrivacyModal;
